import { useState } from "react";
import { ClipboardPaste } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useTran, useFormDirty, useUnsavedChanges } from "@/hooks";
import { UnsavedChangesDialog } from "@/components/common";

const normalizeNumberInput = (value) => {
  const raw = String(value ?? "");
  if (raw === "") return "";
  const normalized = raw.replace(/[^\d.-]/g, "").replace(/(?!^)-/g, "");
  return normalized.replace(/(\..*)\./g, "$1");
};

const handleKeyDown = (event) => {
  const { key } = event;
  if (
    [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      ".",
      "-",
    ].includes(key)
  )
    return;
  if (
    (event.ctrlKey || event.metaKey) &&
    ["a", "c", "v", "x"].includes(key.toLowerCase())
  )
    return;
  if (!/^\d$/.test(key)) event.preventDefault();
};

const SetSameValueToAllMonthsDialog = ({
  open,
  onOpenChange,
  onApply,
  orgName,
  disclosureCode,
  disclosureTitle,
  unit,
  loading = false,
}) => {
  const { t } = useTran();
  const [value, setValue] = useState("");
  const isDirty = useFormDirty(value, open);
  const { showDialog, confirmDiscard, cancelDiscard, guardAction } =
    useUnsavedChanges(isDirty, { blockNavigation: false });

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      guardAction(() => {
        setValue("");
        onOpenChange(false);
      });
      return;
    }
    onOpenChange(nextOpen);
  };

  const handleApply = () => {
    onApply(value);
    setValue("");
  };

  const hasValue = value !== "" && value !== null && value !== undefined;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5 text-primary" />
              {t("Set Value to All Months")}
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-lg border border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs font-mono">
                {disclosureCode}
              </Badge>
              <span className="text-sm font-semibold">{disclosureTitle}</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("This value will be applied to all 12 months for")}{" "}
              <strong>{orgName}</strong>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Input
              type="number"
              inputMode="decimal"
              step="any"
              placeholder={t("e.g. 100")}
              value={value}
              onChange={(e) => setValue(normalizeNumberInput(e.target.value))}
              onKeyDown={handleKeyDown}
              disabled={loading}
              className="w-40"
              autoFocus
            />
            {unit ? (
              <span className="text-sm text-muted-foreground">{unit}</span>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              {t("Cancel")}
            </Button>
            <Button onClick={handleApply} disabled={loading || !hasValue}>
              {t("Apply to All")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </>
  );
};

export default SetSameValueToAllMonthsDialog;
